import Button from "devextreme-react/button";
import NumberBox from "devextreme-react/number-box";
import SelectBox from "devextreme-react/select-box";
import { Tooltip } from "devextreme-react/tooltip";
import React, { useEffect, useMemo, useState } from "react";
import { getTriggerTargets } from "../../../api/v2/TasklistsApi";
import { DefaultModuleDto } from "../../../dtos/DefaultModuleDto";
import { DefaultPhaseDto } from "../../../dtos/DefaultPhaseDto";
import { DefaultStepDto } from "../../../dtos/DefaultStepDto";
import { Modules } from "../../../dtos/Modules";
import { PhaseStatus } from "../../../dtos/PhaseStatus";
import { PhaseWorkflowType } from "../../../dtos/PhaseWorkflowType";
import { StepStatus } from "../../../dtos/StepStatus";
import { TriggerActionConfig } from "../../../dtos/TriggerActionConfig";
import TreeSelect from "../../shared/TreeSelect";
import { getPhaseStatusInfo, getStepStatusInfo } from "../../shared/Utils";
import { useTriggerTargetTree } from "./hooks/useTriggerTargetTree";

type Props = {
    task: DefaultPhaseDto | null;
    subtask: DefaultStepDto | null;
    // unused after the cross-module rewrite, kept to avoid touching every caller right now
    allPhaseTypes?: DefaultPhaseDto[];
    // The edited task's workflow type (for a subtask, its parent task's). Decides which events can
    // be reacted to and which field a start-date rule writes.
    workflowType?: PhaseWorkflowType | null;
    onSave: (data: DefaultPhaseDto | DefaultStepDto) => Promise<void>;
};

// Trigger/action/parameter constants must match the C# enum names exactly.
const PHASE_COMPLETED_TRIGGER = "PhaseCompletedTrigger";
const STEP_COMPLETED_TRIGGER = "StepCompletedTrigger";
const PHASE_CHILD_DOCUMENT_UPLOADED_TRIGGER = "PhaseChildDocumentUploadedTrigger";
const STEP_DOCUMENTS_FILLED_TRIGGER = "StepDocumentsFilledTrigger";
const GRANT_STATUS_CHANGED_TRIGGER = "GrantStatusChangedTrigger";
const SET_PHASE_START_DATE_ACTION = "SetPhaseStartDateAction";
const SET_PHASE_SUBMISSION_DATE_ACTION = "SetPhaseSubmissionDateAction";
const SET_PHASE_COMPLETION_DATE_ACTION = "SetPhaseCompletionDateAction";
const SET_PHASE_STATUS_ACTION = "SetPhaseStatusAction";
const SET_STEP_START_DATE_ACTION = "SetStepStartDateAction";
const SET_STEP_COMPLETION_DATE_ACTION = "SetStepCompletionDateAction";
const SET_STEP_STATUS_ACTION = "SetStepStatusAction";
const CREATE_MODULE_ACTION = "CreateModuleAction";
const PERFORM_PHASE_ACTION = "PerformPhaseAction";
const TARGET_PHASE_ID_KEY = "TargetCoreDefaultPhaseId";
const TARGET_PHASE_ACTION_KEY = "TargetPhaseAction";
const NEW_GRANT_STATUS_KEY = "NewGrantStatus";
const TARGET_STEP_ID_KEY = "TargetCoreDefaultStepId";
const TARGET_STATUS_KEY = "TargetStatus";
const TARGET_MODULE_KEY = "TargetModule";
const DATE_SOURCE_KEY = "DateSource";
const PHASE_COMPLETION_DATE = "PhaseCompletionDate";
const STEP_COMPLETION_DATE = "StepCompletionDate";
const LAST_DOCUMENT_DATE = "LastDocumentDate";
const BUDGET_START_DATE = "BudgetStartDate";
const PHASE_SUBMISSION_DATE = "PhaseSubmissionDate";
const CURRENT_DATE = "CurrentDate";
const SOURCE_PHASE_ID_KEY = "SourceCoreDefaultPhaseId";
const DATE_OFFSET_VALUE_KEY = "DateOffsetValue";
const DATE_OFFSET_UNIT_KEY = "DateOffsetUnit";

// Must match the C# DateOffsetUnits enum names.
const OFFSET_UNIT_OPTIONS = [
    { value: "Days", label: "days" },
    { value: "Weeks", label: "weeks" },
    { value: "Months", label: "months" },
    { value: "Years", label: "years" }
];
const DEFAULT_OFFSET_UNIT = "Days";

// Must match the C# GrantStatus enum names.
const GRANT_STATUS_OPTIONS = [
    { value: "APPLICATION", label: "Application" },
    { value: "COUNCILREVIEW", label: "Council Review" },
    { value: "AWARDPREPARED", label: "Award Prepared" },
    { value: "TOBEPAID", label: "To Be Paid" },
    { value: "AWARDED", label: "Awarded" }
];

// The task actions a rule may run. Must match the C# PhaseAction enum names. Add Subtask and
// Generate Report need a person at the screen, so they are not offered.
const PHASE_ACTION_OPTIONS = [
    { value: "RELEASE", label: "Release" },
    { value: "SUBMIT", label: "Submit" },
    { value: "REQUESTREVISION", label: "Request Revision" },
    { value: "APPROVE", label: "Approve" }
];

// The base a date rule computes from, before its offset. Current date and NoA date are offered
// everywhere; the event-specific sources are added per trigger (ticket 1039).
const DATE_SOURCE_LABELS: Record<string, string> = {
    [CURRENT_DATE]: "current date",
    [BUDGET_START_DATE]: "NoA date",
    [LAST_DOCUMENT_DATE]: "last document date",
    [PHASE_COMPLETION_DATE]: "task completion date",
    [PHASE_SUBMISSION_DATE]: "task start date",
    [STEP_COMPLETION_DATE]: "subtask completion date"
};

const EVENT_DATE_SOURCE: Record<string, string> = {
    [PHASE_COMPLETED_TRIGGER]: PHASE_COMPLETION_DATE,
    [STEP_COMPLETED_TRIGGER]: STEP_COMPLETION_DATE,
    [PHASE_CHILD_DOCUMENT_UPLOADED_TRIGGER]: LAST_DOCUMENT_DATE
};

// A rule that already names a source outside this list (a seeded cross-phase one, say) keeps it, so
// opening and saving cannot quietly rewrite it.
function dateSourceOptionsFor(trigger: string, current: string): { value: string; label: string }[] {
    const values = [CURRENT_DATE, BUDGET_START_DATE];
    const eventSource = EVENT_DATE_SOURCE[trigger];
    if (eventSource) values.push(eventSource);
    if (current && !values.includes(current)) values.push(current);
    return values.map(v => ({ value: v, label: DATE_SOURCE_LABELS[v] ?? v }));
}

// Unlimited is the behavior every rule had before a limit could be set, so it stays the default.
const RUN_LIMIT_UNLIMITED = "unlimited";
const RUN_LIMIT_ONCE = "once";
const RUN_LIMIT_OPTIONS = [
    { value: RUN_LIMIT_UNLIMITED, label: "every time" },
    { value: RUN_LIMIT_ONCE, label: "once only" }
];


type FieldKind = "startDate" | "completionDate" | "status" | "createModule" | "taskAction";

const FIELD_OPTIONS: { value: FieldKind; label: string }[] = [
    { value: "startDate", label: "set start date" },
    { value: "completionDate", label: "set completion date" },
    { value: "status", label: "set status" },
    { value: "taskAction", label: "run task action" },
    { value: "createModule", label: "create module" }
];

// Action names that this tab manages. Anything else is preserved untouched.
const MANAGED_ACTIONS = new Set([
    SET_PHASE_START_DATE_ACTION,
    SET_PHASE_SUBMISSION_DATE_ACTION,
    SET_PHASE_COMPLETION_DATE_ACTION,
    SET_PHASE_STATUS_ACTION,
    SET_STEP_START_DATE_ACTION,
    SET_STEP_COMPLETION_DATE_ACTION,
    SET_STEP_STATUS_ACTION,
    CREATE_MODULE_ACTION,
    PERFORM_PHASE_ACTION
]);


// What a rule can react to. A task listens to two different kinds of event and they are not
// interchangeable: PhaseCompleted is raised at phase level (a user approving the task, or - in the
// None workflow only - its subtasks all reaching approved), while PhaseDocumentUploaded is raised by
// any one child subtask receiving a file. Keeping them apart is the point of this list.
// The event is chosen per rule (ticket 1039) rather than by which section a rule was added under, so
// adding an event no longer means adding a section.
type TriggerOption = { trigger: string; title: string; description: string };

// What each action does once its event fires. Shown on the row so the behaviour an admin cannot
// infer from "set completion date of X" - when it recalculates, and what stops it - is on screen.
const ACTION_HELP: Record<string, string> = {
    startDate: "Sets the target's start date. Recalculated each time the event fires - unless the rule runs once only - so a later firing can move it. A date edited by hand is left alone until it is reverted.",
    completionDate: "Sets the target's completion date. Recalculated each time the event fires - unless the rule runs once only - so a later firing can move it. A date edited by hand is left alone until it is reverted.",
    status: "Moves the target to the chosen status. Applied each time the event fires; no effect if it is already there.",
    createModule: "Adds the module to the grant. Skipped when the grant already has it.",
    taskAction: "Runs the action as if its button were pressed, with the same emails. Skipped, and logged, when the task does not offer that action at its current status - a Release on a completed task, say."
};

const TASK_COMPLETED_OPTION: TriggerOption = {
    trigger: PHASE_COMPLETED_TRIGGER,
    title: "this task is completed",
    description: "Runs once the task reaches approved."
};
const TASK_CHILD_UPLOAD_OPTION: TriggerOption = {
    trigger: PHASE_CHILD_DOCUMENT_UPLOADED_TRIGGER,
    title: "a file is uploaded to any of its subtasks",
    description: "Runs on every upload into any subtask, so the rule is configured here rather than on each one."
};
const SUBTASK_COMPLETED_OPTION: TriggerOption = {
    trigger: STEP_COMPLETED_TRIGGER,
    title: "this subtask is completed",
    description: "Runs once the subtask reaches approved."
};
const SUBTASK_DOCUMENTS_FILLED_OPTION: TriggerOption = {
    trigger: STEP_DOCUMENTS_FILLED_TRIGGER,
    title: "all of its documents have been uploaded",
    description: "Runs on the upload that fills the last named document. Only for a subtask that names its documents on the Files tab - one that takes anything has nothing to be complete against."
};

const GRANT_STATUS_CHANGED_OPTION: TriggerOption = {
    trigger: GRANT_STATUS_CHANGED_TRIGGER,
    title: "the grant status changes",
    description: "Runs when the nightly IMPAC II import finds the grant has moved to the chosen status. No user made the change, so none is named on what the rule does."
};

// What an item can react to in the tasklist. A task hears about its own completion and about any of
// its subtasks receiving a file; a subtask hears about its own completion and about the last of its
// named documents arriving.
function triggerOptionsFor(isTask: boolean): TriggerOption[] {
    if (!isTask) return [SUBTASK_COMPLETED_OPTION, SUBTASK_DOCUMENTS_FILLED_OPTION];
    return [TASK_COMPLETED_OPTION, TASK_CHILD_UPLOAD_OPTION];
}

// Events from outside the tasklist, such as the grant's status changing in IMPAC II. Only a task
// hears them, and its rules for them always act on that task, so they get their own section with the
// target fixed.
const EXTERNAL_TRIGGER_OPTIONS: TriggerOption[] = [GRANT_STATUS_CHANGED_OPTION];
const EXTERNAL_TRIGGERS = new Set(EXTERNAL_TRIGGER_OPTIONS.map(o => o.trigger));
const isExternal = (row: TriggerRow) => EXTERNAL_TRIGGERS.has(row.trigger);

// Rules on any other event are left exactly as they were read, so this tab cannot drop one it does
// not know about.
const SUPPORTED_TRIGGERS = new Set([
    PHASE_COMPLETED_TRIGGER,
    STEP_COMPLETED_TRIGGER,
    PHASE_CHILD_DOCUMENT_UPLOADED_TRIGGER,
    STEP_DOCUMENTS_FILLED_TRIGGER,
    GRANT_STATUS_CHANGED_TRIGGER
]);

// A task may target itself for an event it did not raise itself: a file landing in a child, or its
// grant changing status. Reacting to its own completion by changing itself is circular.
const TRIGGERS_ALLOWING_SELF = new Set([
    PHASE_CHILD_DOCUMENT_UPLOADED_TRIGGER,
    GRANT_STATUS_CHANGED_TRIGGER
]);

// Ticket 210 renamed the submission field to "Start Date" for the document phases, so a start-date
// rule on those writes Phase.Submission; everywhere else it writes Phase.StartDate.
const startDateActionFor = (uploadOnly: boolean) =>
    uploadOnly ? SET_PHASE_SUBMISSION_DATE_ACTION : SET_PHASE_START_DATE_ACTION;

// One row in the editor. targetKey encodes both the type (phase/step) and id, so the
// cross-module SelectBox can return a single string. e.g. "phase:42" or "step:101".
export type TriggerRow = {
    rowId: string;
    // Which event this rule reacts to. Rows are no longer all one trigger.
    trigger: string;
    // Empty until the user picks an action; dependent controls stay hidden while empty.
    field: FieldKind | "";
    targetKey: string;
    targetStatus: string;
    // Modules enum name (e.g. "DC") for the "create module" field. Empty for other fields.
    targetModule: string;
    // PhaseAction enum name (e.g. "RELEASE") for the "run task action" field. Empty for other fields.
    phaseAction: string;
    // GrantStatus enum name the grant status event waits for. Empty for other events.
    grantStatus: string;
    // Preserved verbatim from the stored config. Order sequences the rules within a trigger (the
    // seeded cross-phase date chain depends on it) and extraParams carries settings this tab has no
    // control for - the date source and the phase a date is copied from.
    order: number;
    // Which value the rule writes, and optionally the phase it is read from. The source phase is
    // not editable here - it rides along so a seeded cross-phase rule is written back exactly as it
    // was read.
    dateSource: string;
    sourceKey: string;
    // How far to shift the resolved date. An empty value means no offset, whatever the unit says.
    offsetValue: string;
    offsetUnit: string;
    // null = unlimited. 1 = runs once per task/subtask and then stops.
    runLimit: number | null;
    extraParams: { key: string; value: string }[];
};

type TargetOption = {
    key: string;
    moduleName: string;
    phaseName: string;
    stepName?: string;
    label: string;
    isStep: boolean;
    phaseId: number;
    stepId?: number;
    // The task being edited. Selectable only for events raised by its children.
    isSelf?: boolean;
    // For phase targets: the phase's own workflow type. For step targets: the parent phase's workflow type.
    // Drives which status values are valid for this target.
    workflowType: PhaseWorkflowType | null;
    // Phase targets only: whether its start date lives in Phase.Submission (document phases).
    uploadOnly?: boolean;
};

const newRowId = () => `r-${Math.random().toString(36).slice(2, 10)}`;

// The next free order within an event. Counting the rules is not enough - deleting one from the
// middle leaves a gap, and a reused order would give two rules the same identity in the run log
// as well as an ambiguous execution sequence.
// Everything about a rule except its identity, so an edited rule can be told from an untouched one.
function ruleSignature(row: TriggerRow): string {
    const { rowId, ...rest } = row;
    return JSON.stringify(rest);
}

// A rule that is missing something is kept open, so the row itself is the only place the user has
// to look. Null when every rule is complete.
function firstIncomplete(rows: TriggerRow[]): string | null {
    return rows.find(r => ruleProblems(r).length > 0)?.rowId ?? null;
}

export function nextOrder(rows: TriggerRow[], trigger: string, excludeRowId?: string): number {
    const orders = rows
        .filter(r => r.trigger === trigger && r.rowId !== excludeRowId)
        .map(r => r.order);
    return orders.length === 0 ? 1 : Math.max(...orders) + 1;
}

// What a rule is still missing, in the order its controls appear. Save is blocked while any rule
// has an entry here, and the same list is what the tab shows instead of a silently dead button.
function ruleProblems(row: TriggerRow): string[] {
    const problems: string[] = [];
    if (!row.trigger) problems.push("choose an event");
    if (row.trigger === GRANT_STATUS_CHANGED_TRIGGER && !row.grantStatus) problems.push("choose a grant status");
    if (!row.field) {
        problems.push("choose an action");
        return problems;
    }
    if (row.field === "createModule") {
        if (!row.targetModule) problems.push("choose a module");
        return problems;
    }
    if (row.field === "taskAction" && !row.phaseAction) problems.push("choose a task action");
    if (!row.targetKey) problems.push("choose a target");
    if (row.field === "status" && !row.targetStatus) problems.push("choose a status");
    return problems;
}

const CELL_STYLE: React.CSSProperties = { padding: "8px", verticalAlign: "middle" };
const HEADER_CELL_STYLE: React.CSSProperties = {
    padding: "8px", textAlign: "left", fontSize: "12px", fontWeight: 600,
    color: "#666", textTransform: "uppercase", borderBottom: "2px solid #ddd"
};
const PROBLEM_COLOR = "#a94442";

// Keys this tab edits directly. Everything else on a stored config rides along untouched.
const CONTROLLED_PARAM_KEYS = new Set([
    TARGET_PHASE_ID_KEY, TARGET_STEP_ID_KEY, TARGET_STATUS_KEY, TARGET_MODULE_KEY,
    DATE_SOURCE_KEY, SOURCE_PHASE_ID_KEY, DATE_OFFSET_VALUE_KEY, DATE_OFFSET_UNIT_KEY,
    TARGET_PHASE_ACTION_KEY, NEW_GRANT_STATUS_KEY
]);

// Maps a stored TriggerActionConfig to the editor row shape, or null if it isn't a row we manage.
function configToRow(c: TriggerActionConfig): TriggerRow | null {
    const param = (key: string) => c.parameters?.find(p => p.key === key)?.value ?? "";
    const sourcePhaseId = param(SOURCE_PHASE_ID_KEY);
    const base = {
        rowId: newRowId(),
        trigger: c.trigger,
        order: c.order ?? 1,
        dateSource: param(DATE_SOURCE_KEY),
        sourceKey: sourcePhaseId ? `phase:${sourcePhaseId}` : "",
        offsetValue: param(DATE_OFFSET_VALUE_KEY),
        offsetUnit: param(DATE_OFFSET_UNIT_KEY) || DEFAULT_OFFSET_UNIT,
        runLimit: c.runLimit ?? null,
        extraParams: (c.parameters ?? []).filter(p => !CONTROLLED_PARAM_KEYS.has(p.key)).map(p => ({ key: p.key, value: p.value })),
        phaseAction: "",
        grantStatus: param(NEW_GRANT_STATUS_KEY),
    };
    switch (c.action) {
        case SET_PHASE_START_DATE_ACTION:
        case SET_PHASE_SUBMISSION_DATE_ACTION:
            return { ...base, field: "startDate", targetKey: `phase:${param(TARGET_PHASE_ID_KEY)}`, targetStatus: "", targetModule: "" };
        case SET_PHASE_COMPLETION_DATE_ACTION:
            return { ...base, field: "completionDate", targetKey: `phase:${param(TARGET_PHASE_ID_KEY)}`, targetStatus: "", targetModule: "" };
        case SET_PHASE_STATUS_ACTION:
            return { ...base, field: "status", targetKey: `phase:${param(TARGET_PHASE_ID_KEY)}`, targetStatus: param(TARGET_STATUS_KEY), targetModule: "" };
        case SET_STEP_START_DATE_ACTION:
            return { ...base, field: "startDate", targetKey: `step:${param(TARGET_STEP_ID_KEY)}`, targetStatus: "", targetModule: "" };
        case SET_STEP_COMPLETION_DATE_ACTION:
            return { ...base, field: "completionDate", targetKey: `step:${param(TARGET_STEP_ID_KEY)}`, targetStatus: "", targetModule: "" };
        case SET_STEP_STATUS_ACTION:
            return { ...base, field: "status", targetKey: `step:${param(TARGET_STEP_ID_KEY)}`, targetStatus: param(TARGET_STATUS_KEY), targetModule: "" };
        case CREATE_MODULE_ACTION:
            return { ...base, field: "createModule", targetKey: "", targetStatus: "", targetModule: param(TARGET_MODULE_KEY) };
        case PERFORM_PHASE_ACTION:
            return { ...base, field: "taskAction", targetKey: `phase:${param(TARGET_PHASE_ID_KEY)}`, targetStatus: "", targetModule: "", phaseAction: param(TARGET_PHASE_ACTION_KEY) };
        default:
            return null;
    }
}

// The event's own condition goes on every rule for that event, whatever the action.
function rowToConfig(row: TriggerRow, targetUploadOnly: boolean, dateSource: string): TriggerActionConfig | null {
    const config = actionToConfig(row, targetUploadOnly, dateSource);
    if (config && row.trigger === GRANT_STATUS_CHANGED_TRIGGER && row.grantStatus) {
        config.parameters = [...config.parameters, { key: NEW_GRANT_STATUS_KEY, value: row.grantStatus }];
    }
    return config;
}

// Rebuilds a stored config from a row. The row's own trigger and order are used, not the tab's -
// rewriting either would move a rule onto an event that never fires, or collapse the ordering the
// seeded cross-phase date chain depends on. Parameters the tab has no control for are written back
// as they were read; the default date source is only supplied when the row carries none.
function actionToConfig(row: TriggerRow, targetUploadOnly: boolean, dateSource: string): TriggerActionConfig | null {
    const triggerType = row.trigger;
    const passThrough = row.extraParams;
    // The source is now stated on the row. A source phase is written only when one is chosen -
    // absent means "read the value from the target itself", which is how the seeded rules read.
    const sourcePhaseId = row.sourceKey.startsWith("phase:") ? row.sourceKey.slice("phase:".length) : "";
    // An offset is written only once an amount is stated - a bare unit means no shift, and writing
    // it would make an untouched rule read as changed.
    const offsetParams = row.offsetValue
        ? [
            { key: DATE_OFFSET_VALUE_KEY, value: row.offsetValue },
            { key: DATE_OFFSET_UNIT_KEY, value: row.offsetUnit || DEFAULT_OFFSET_UNIT }
        ]
        : [];
    const dateParams = [
        { key: DATE_SOURCE_KEY, value: row.dateSource || dateSource },
        ...(sourcePhaseId ? [{ key: SOURCE_PHASE_ID_KEY, value: sourcePhaseId }] : []),
        ...offsetParams,
        ...passThrough
    ];
    if (row.field === "createModule") {
        if (!row.targetModule) return null;
        return {
            trigger: triggerType,
            action: CREATE_MODULE_ACTION,
            order: row.order,
            runLimit: row.runLimit,
            parameters: [
                { key: TARGET_MODULE_KEY, value: row.targetModule },
                ...passThrough
            ]
        };
    }

    if (!row.targetKey) return null;
    const [kind, idStr] = row.targetKey.split(":");
    if (!idStr) return null;

    if (row.field === "taskAction") {
        if (kind !== "phase" || !row.phaseAction) return null;
        return {
            trigger: triggerType,
            action: PERFORM_PHASE_ACTION,
            order: row.order,
            runLimit: row.runLimit,
            parameters: [
                { key: TARGET_PHASE_ID_KEY, value: idStr },
                { key: TARGET_PHASE_ACTION_KEY, value: row.phaseAction },
                ...passThrough
            ]
        };
    }

    if (row.field === "startDate") {
        const isPhase = kind === "phase";
        return {
            trigger: triggerType,
            action: isPhase ? startDateActionFor(targetUploadOnly) : SET_STEP_START_DATE_ACTION,
            order: row.order,
            runLimit: row.runLimit,
            parameters: [
                { key: isPhase ? TARGET_PHASE_ID_KEY : TARGET_STEP_ID_KEY, value: idStr },
                ...dateParams
            ]
        };
    }
    if (row.field === "completionDate") {
        const isPhase = kind === "phase";
        return {
            trigger: triggerType,
            action: isPhase ? SET_PHASE_COMPLETION_DATE_ACTION : SET_STEP_COMPLETION_DATE_ACTION,
            order: row.order,
            runLimit: row.runLimit,
            parameters: [
                { key: isPhase ? TARGET_PHASE_ID_KEY : TARGET_STEP_ID_KEY, value: idStr },
                ...dateParams
            ]
        };
    }
    if (row.field === "status" && row.targetStatus) {
        if (kind === "phase") {
            return {
                trigger: triggerType,
                action: SET_PHASE_STATUS_ACTION,
                order: row.order,
                runLimit: row.runLimit,
                parameters: [
                    { key: TARGET_PHASE_ID_KEY, value: idStr },
                    { key: TARGET_STATUS_KEY, value: row.targetStatus },
                    ...passThrough
                ]
            };
        }
        if (kind === "step") {
            return {
                trigger: triggerType,
                action: SET_STEP_STATUS_ACTION,
                order: row.order,
                runLimit: row.runLimit,
                parameters: [
                    { key: TARGET_STEP_ID_KEY, value: idStr },
                    { key: TARGET_STATUS_KEY, value: row.targetStatus },
                    ...passThrough
                ]
            };
        }
    }
    return null;
}

const PHASE_WORKFLOW_BADGES: Record<PhaseWorkflowType, string> = {
    [PhaseWorkflowType.None]: "NONE",
    [PhaseWorkflowType.Step]: "STEP",
    [PhaseWorkflowType.Phase]: "PHASE",
    [PhaseWorkflowType.PhaseHybrid]: "PHASE HYBRID",
    [PhaseWorkflowType.StepHybrid]: "STEP HYBRID"
};

const phaseWorkflowBadge = (item: TargetOption): string | undefined => {
    if (item.workflowType == null) return undefined;
    return PHASE_WORKFLOW_BADGES[item.workflowType];
};

// Thin adapter so the row renderer (a plain function, not a component) can use the hook.
function RowTargetSelect({
    targets,
    value,
    isSelectable,
    disabledReason,
    placeholder,
    onChange
}: {
    targets: TargetOption[];
    value: string;
    isSelectable?: (item: TargetOption) => boolean;
    disabledReason?: string;
    placeholder?: string;
    onChange: (key: string) => void;
}) {
    const { nodes, selectedLabel } = useTriggerTargetTree(targets, value, {
        isSelectable,
        disabledReason,
        getPhaseBadge: phaseWorkflowBadge
    });
    return (
        <TreeSelect
            nodes={nodes}
            value={value}
            selectedLabel={selectedLabel}
            onChange={onChange}
            placeholder={placeholder ?? "Select target..."}
        />
    );
}

export default function TemplateTriggersTab({ task, subtask, workflowType, onSave }: Props) {
    const isTask = !!task;
    const editingItem = task ?? subtask;
    const dateSource = isTask ? PHASE_COMPLETION_DATE : STEP_COMPLETION_DATE;
    const triggerLabel = isTask ? "task" : "subtask";
    const triggerOptions = triggerOptionsFor(isTask);
    const externalTriggerOptions = isTask ? EXTERNAL_TRIGGER_OPTIONS : [];
    const allTriggerOptions = [...triggerOptions, ...externalTriggerOptions];
    // What an external rule acts on: the task being edited.
    const selfKey = task ? `phase:${task.id}` : "";

    const currentTriggerActions = (isTask ? task?.settings?.triggerActions : subtask?.settings?.triggerActions) ?? [];

    // Every managed action on a trigger this tab understands becomes a row. Anything else - an
    // unknown action, or a trigger this editor has no vocabulary for - is carried through untouched.
    const isEditable = (ta: TriggerActionConfig) =>
        MANAGED_ACTIONS.has(ta.action) && SUPPORTED_TRIGGERS.has(ta.trigger);
    const editableConfigs = (configs: TriggerActionConfig[] | undefined) =>
        (configs ?? []).filter(isEditable);

    const initialRows = () =>
        editableConfigs(currentTriggerActions).map(configToRow).filter((r): r is TriggerRow => r !== null);

    const [rows, setRows] = useState<TriggerRow[]>(initialRows);
    // The same rows as they were last saved, matched by rowId. Reverting one rule reads from here,
    // so "discard everything" is not the only way back.
    const [savedRows, setSavedRows] = useState<TriggerRow[]>(() => rows);
    const [savedSnapshot, setSavedSnapshot] = useState<string>(() => JSON.stringify(currentTriggerActions ?? []));
    const [modules, setModules] = useState<DefaultModuleDto[] | null>(null);
    // The rule being edited in place. Null means every rule is showing its plain-language summary,
    // which is how the tab opens - a wall of dropdowns was the thing that made a long list unreadable.
    // The effect below opens an incomplete rule; it cannot be seeded here, because building rows a
    // second time would generate row ids that match nothing in `rows`.
    const [editingRowId, setEditingRowId] = useState<string | null>(null);

    // Reload rows when the edited item changes.
    useEffect(() => {
        loadRows();
    }, [isTask ? task?.id : subtask?.id]);

    // Rows, their baseline and the open rule always move together - a baseline keyed on row ids
    // from a different build would make every rule look changed.
    const loadRows = () => {
        const next = editableConfigs(currentTriggerActions).map(configToRow).filter((r): r is TriggerRow => r !== null);
        setRows(next);
        setSavedRows(next);
        setEditingRowId(firstIncomplete(next));
        setSavedSnapshot(JSON.stringify(currentTriggerActions ?? []));
    };

    useEffect(() => {
        (async () => {
            const result = await getTriggerTargets();
            if (result?.data) setModules(result.data);
        })();
    }, []);

    // Build the cross-module dropdown items: each module contributes its phases and steps.
    const targetOptions = useMemo<TargetOption[]>(() => {
        if (!modules) return [];
        const opts: TargetOption[] = [];
        const editingPhaseId = task?.id ?? null;
        for (const mod of modules) {
            for (const phase of mod.phaseTypes ?? []) {
                const phaseWorkflowType = phase.settings?.workflowType ?? null;
                // A task can target itself, but only for an event its children raise - a file
                // landing in a subtask setting this task's own dates is the whole point of the
                // seeded chain. Reacting to its own completion by setting its own completion is
                // circular, so that stays blocked (per row, in rowSelectablePredicate).
                opts.push({
                    key: `phase:${phase.id}`,
                    moduleName: mod.name,
                    phaseName: phase.name,
                    label: `${mod.name} › ${phase.name}`,
                    isStep: false,
                    phaseId: phase.id,
                    workflowType: phaseWorkflowType,
                    uploadOnly: phase.settings?.uploadOnlySubtasks === true,
                    isSelf: editingPhaseId != null && phase.id === editingPhaseId
                });
                for (const step of phase.stepTypes ?? []) {
                    opts.push({
                        key: `step:${step.id}`,
                        moduleName: mod.name,
                        phaseName: phase.name,
                        stepName: step.name,
                        label: `${mod.name} › ${phase.name} › ${step.name}`,
                        isStep: true,
                        phaseId: phase.id,
                        stepId: step.id,
                        workflowType: phaseWorkflowType
                    });
                }
            }
        }
        return opts;
    }, [modules, task?.id]);

    // Modules that can be provisioned by the "create module" action. Value is the
    // Modules enum name (e.g. "DC") so it round-trips to the C# CreateModuleAction.
    const moduleOptions = useMemo<{ value: string; label: string }[]>(() => {
        if (!modules) return [];
        return modules
            .map(m => ({ value: Modules[m.id] as string, label: m.name }))
            .filter(o => !!o.value);
    }, [modules]);

    // Step-workflow tasks have no parent status — each subtask is its own tasklist.
    // Setting status on the task as a whole isn't a real concept here.
    const isStatusFieldApplicable = (opt: TargetOption): boolean => {
        return !(opt.workflowType === PhaseWorkflowType.Step && !opt.isStep);
    };

    // Predicate handed to the tree hook: which targets are clickable for this row.
    // Returning false keeps the item visible in the tree (hierarchy stays intact) but disables it.
    const rowSelectablePredicate = (row: TriggerRow) => (opt: TargetOption): boolean => {
        if (opt.isSelf && !TRIGGERS_ALLOWING_SELF.has(row.trigger)) return false;
        if (row.field === "status") return isStatusFieldApplicable(opt);
        // Task actions belong to tasks; a subtask has none.
        if (row.field === "taskAction") return !opt.isStep;
        return true;
    };

    const disabledTargetReason = (row: TriggerRow): string | undefined => {
        if (row.field === "status") return 'task not selectable with "status" selected';
        if (row.field === "taskAction") return "subtasks have no task actions";
        return undefined;
    };

    // For status dropdown: phase target = PhaseStatus values, step target = StepStatus values,
    // filtered by the target's workflow type so users only see valid options.
    //
    // None workflow has only three meaningful states (NOT_STARTED, SETUP=In Progress, APPROVED=Complete)
    // plus INACTIVE. The grantee/NIH back-and-forth statuses (READY, GRANTEE_*, PENDING*) don't apply.
    const isSimpleStatusSet = (workflowType: PhaseWorkflowType | null): boolean => {
        return workflowType === PhaseWorkflowType.None;
    };

    const isAllowedStepStatus = (status: StepStatus, workflowType: PhaseWorkflowType | null): boolean => {
        if (!isSimpleStatusSet(workflowType)) return true;
        switch (status) {
            case StepStatus.NOT_STARTED:
            case StepStatus.SETUP:
            case StepStatus.APPROVED:
            case StepStatus.INACTIVE:
                return true;
            default:
                return false;
        }
    };

    const isAllowedPhaseStatus = (status: PhaseStatus, workflowType: PhaseWorkflowType | null): boolean => {
        if (!isSimpleStatusSet(workflowType)) return true;
        switch (status) {
            case PhaseStatus.NOT_STARTED:
            case PhaseStatus.SETUP:
            case PhaseStatus.APPROVED:
            case PhaseStatus.INACTIVE:
                return true;
            default:
                return false;
        }
    };

    const statusOptionsForRow = (row: TriggerRow): { value: string; label: string }[] => {
        const opt = targetOptions.find(o => o.key === row.targetKey);
        if (!opt) return [];
        const wf = opt.workflowType;
        if (opt.isStep) {
            return Object.values(StepStatus)
                .filter(v => typeof v === "number")
                .filter(v => isAllowedStepStatus(v as StepStatus, wf))
                .map(v => ({ value: StepStatus[v as number], label: getStepStatusInfo(v as StepStatus, null, wf).text }));
        }
        return Object.values(PhaseStatus)
            .filter(v => typeof v === "number")
            .filter(v => isAllowedPhaseStatus(v as PhaseStatus, wf))
            .map(v => ({ value: PhaseStatus[v as number], label: getPhaseStatusInfo(v as PhaseStatus, null, wf).text }));
    };

    const buildConfigsForSave = (): TriggerActionConfig[] => {
        // Keep configs we don't manage (none today, but future-proof).
        const otherActions = (currentTriggerActions ?? []).filter(ta => !isEditable(ta));
        const built = rows
            // Fall back to the edited task's own flag while the target list is still loading; the
            // seeded rules point within their own module, so the two agree there.
            .map(r => rowToConfig(r, targetOptions.find(o => o.key === r.targetKey)?.uploadOnly ?? task?.settings?.uploadOnlySubtasks === true, dateSource))
            .filter((c): c is TriggerActionConfig => c !== null);
        return [...otherActions, ...built];
    };

    const isDirty = useMemo(() => {
        return JSON.stringify(buildConfigsForSave()) !== savedSnapshot;
    }, [rows, savedSnapshot, currentTriggerActions]);

    const saveDisabled = rows.some(r => ruleProblems(r).length > 0);

    // A new rule starts on the first event its section offers - the dropdown is then the way to
    // move it. An external rule acts on this task, so it starts pointing here.
    const handleAddRow = (external: boolean) => {
        const trigger = (external ? externalTriggerOptions : triggerOptions)[0]?.trigger ?? "";
        const rowId = newRowId();
        // A new rule has nothing to summarize yet, so it opens ready to edit.
        setEditingRowId(rowId);
        setRows(prev => [...prev, {
            rowId, trigger, field: "", targetKey: external ? selfKey : "",
            targetStatus: "", targetModule: "",
            phaseAction: "", grantStatus: "",
            order: nextOrder(prev, trigger),
            dateSource: defaultDateSourceFor(trigger),
            sourceKey: "",
            offsetValue: "",
            offsetUnit: DEFAULT_OFFSET_UNIT,
            runLimit: null,
            extraParams: []
        }]);
    };

    const handleRemoveRow = (rowId: string) => {
        if (rowId === editingRowId) setEditingRowId(null);
        setRows(prev => prev.filter(r => r.rowId !== rowId));
    };

    // A grant status change carries no date of its own, so its rules default to today.
    const defaultDateSourceFor = (trigger: string) => {
        if (trigger === PHASE_CHILD_DOCUMENT_UPLOADED_TRIGGER) return LAST_DOCUMENT_DATE;
        if (trigger === GRANT_STATUS_CHANGED_TRIGGER) return CURRENT_DATE;
        return dateSource;
    };

    const handleRowChange = (rowId: string, patch: Partial<TriggerRow>) => {
        setRows(prev => prev.map(r => {
            if (r.rowId !== rowId) return r;
            const next = { ...r, ...patch };
            // Moving a rule to another event puts it at the end of that event's chain, and swaps a
            // date source the new event cannot produce for that event's own.
            if (patch.trigger !== undefined && patch.trigger !== r.trigger) {
                next.order = nextOrder(prev, next.trigger, r.rowId);
                const sources = dateSourceOptionsFor(next.trigger, "").map(o => o.value);
                if (!sources.includes(next.dateSource)) next.dateSource = defaultDateSourceFor(next.trigger);
                // A target the new event may not point at (the task itself, say) is dropped.
                const opt = targetOptions.find(o => o.key === next.targetKey);
                if (opt && !rowSelectablePredicate(next)(opt)) next.targetKey = "";
            }
            if (next.trigger !== GRANT_STATUS_CHANGED_TRIGGER) next.grantStatus = "";
            // If field changed, drop the target if it's no longer selectable under the new field
            // (e.g. switching to "status" while a Step-workflow phase is selected).
            if (patch.field !== undefined && patch.field !== r.field) {
                if (next.field === "createModule") {
                    // "create module" uses a module picker, not a phase/step target.
                    next.targetKey = "";
                    next.targetStatus = "";
                } else {
                    next.targetModule = "";
                    const opt = targetOptions.find(o => o.key === next.targetKey);
                    if (opt && !rowSelectablePredicate(next)(opt)) {
                        next.targetKey = "";
                    }
                }
            }
            // Clear status when target changed or when leaving the status field.
            if (patch.targetKey !== undefined && patch.targetKey !== r.targetKey) {
                next.targetStatus = "";
            }
            if (next.field !== "status") next.targetStatus = "";
            if (next.field !== "taskAction") next.phaseAction = "";
            // An external rule always acts on this task; only "create module" has no target.
            if (isExternal(next) && next.field !== "createModule") next.targetKey = selfKey;
            return next;
        }));
    };

    const handleSave = async () => {
        if (!editingItem) return;
        const updatedTriggerActions = buildConfigsForSave();
        const persistList = updatedTriggerActions.length > 0 ? updatedTriggerActions : null;

        if (isTask && task) {
            await onSave({ ...task, settings: { ...task.settings, triggerActions: persistList } } as DefaultPhaseDto);
        } else if (subtask) {
            await onSave({ ...subtask, settings: { ...subtask.settings, triggerActions: persistList } } as DefaultStepDto);
        }
        setSavedSnapshot(JSON.stringify(updatedTriggerActions));
        // What was just written is the new baseline for a per-rule revert.
        setSavedRows(rows);
    };

    const handleDiscard = () => {
        loadRows();
    };

    // Puts one rule back to its last saved state, leaving every other edit alone. A rule added
    // since the last save has nothing to go back to - deleting it is the way out of that one.
    const savedVersionOf = (rowId: string) => savedRows.find(r => r.rowId === rowId) ?? null;

    const rowHasEdits = (row: TriggerRow) => {
        const saved = savedVersionOf(row.rowId);
        return saved != null && ruleSignature(saved) !== ruleSignature(row);
    };

    const handleRevertRow = (rowId: string) => {
        const saved = savedVersionOf(rowId);
        if (saved == null) return;
        setRows(prev => prev.map(r => (r.rowId === rowId ? { ...saved } : r)));
    };

    const targetLabel = (key: string) => targetOptions.find(o => o.key === key)?.label ?? "";

    const offsetPhrase = (row: TriggerRow): string => {
        const amount = Number(row.offsetValue);
        if (!row.offsetValue || Number.isNaN(amount) || amount === 0) return "";
        const unit = OFFSET_UNIT_OPTIONS.find(u => u.value === (row.offsetUnit || DEFAULT_OFFSET_UNIT))?.label ?? "";
        return ` ${amount > 0 ? "+" : "-"}${Math.abs(amount)} ${unit}`;
    };

    // The whole point of the grid: one plain-language line per rule, so a list of them can be read
    // without opening each one.
    const describeAction = (row: TriggerRow): string => {
        if (!row.field) return "(no action chosen)";
        if (row.field === "createModule") {
            const label = moduleOptions.find(m => m.value === row.targetModule)?.label;
            return label ? `create module ${label}` : "create module (none chosen)";
        }

        const target = isExternal(row) ? "this task" : targetLabel(row.targetKey) || "(no target chosen)";
        if (row.field === "taskAction") {
            const action = PHASE_ACTION_OPTIONS.find(o => o.value === row.phaseAction)?.label ?? "(no action chosen)";
            return `run ${action} on ${target}`;
        }
        if (row.field === "status") {
            const status = statusOptionsForRow(row).find(o => o.value === row.targetStatus)?.label;
            return `set status of ${target} to ${status ?? "(no status chosen)"}`;
        }

        const field = row.field === "startDate" ? "start date" : "completion date";
        const source = DATE_SOURCE_LABELS[row.dateSource] ?? row.dateSource;
        return `set ${field} of ${target} to ${source}${offsetPhrase(row)}`;
    };

    // A rule shows its summary until it is picked, then the same cells carry its controls - the
    // row is the editor, so there is no second place to look for what is being edited.
    const renderWhenCell = (row: TriggerRow, editing: boolean) => {
        const isGrantStatus = row.trigger === GRANT_STATUS_CHANGED_TRIGGER;
        if (!editing) {
            const title = allTriggerOptions.find(o => o.trigger === row.trigger)?.title ?? row.trigger;
            if (!isGrantStatus) return <span>{title}</span>;
            const status = GRANT_STATUS_OPTIONS.find(o => o.value === row.grantStatus)?.label;
            return (
                <span style={{ color: status ? undefined : PROBLEM_COLOR }}>
                    {`${title} to ${status ?? "(no status chosen)"}`}
                </span>
            );
        }
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <SelectBox
                    items={isExternal(row) ? externalTriggerOptions : triggerOptions}
                    displayExpr="title"
                    valueExpr="trigger"
                    value={row.trigger}
                    width="100%"
                    placeholder="Select event..."
                    isValid={!!row.trigger}
                    onValueChanged={e => handleRowChange(row.rowId, { trigger: e.value ?? "" })}
                />
                {isGrantStatus && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span>to</span>
                        <SelectBox
                            items={GRANT_STATUS_OPTIONS}
                            displayExpr="label"
                            valueExpr="value"
                            value={row.grantStatus}
                            width="100%"
                            placeholder="Select status..."
                            isValid={!!row.grantStatus}
                            onValueChanged={e => handleRowChange(row.rowId, { grantStatus: e.value ?? "" })}
                        />
                    </div>
                )}
            </div>
        );
    };

    // An external rule acts on this task. "Set status" is left out where the task has no status of
    // its own (a Step task).
    const selfOption = targetOptions.find(o => o.key === selfKey);
    const externalFieldOptions = FIELD_OPTIONS.filter(o =>
        o.value !== "status" || !selfOption || isStatusFieldApplicable(selfOption));

    const renderThenCell = (row: TriggerRow, editing: boolean) => {
        const problems = ruleProblems(row);
        if (!editing) {
            return (
                <span style={{ color: problems.length > 0 ? PROBLEM_COLOR : undefined }}>
                    {describeAction(row)}
                </span>
            );
        }

        const noAction = !row.field;
        const isCreateModule = row.field === "createModule";
        const isTaskAction = row.field === "taskAction";
        const showStatus = row.field === "status";
        const showDate = row.field === "startDate" || row.field === "completionDate";
        const statusOptions = showStatus ? statusOptionsForRow(row) : [];
        const external = isExternal(row);

        return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <SelectBox
                    items={external ? externalFieldOptions : FIELD_OPTIONS}
                    displayExpr="label"
                    valueExpr="value"
                    value={row.field}
                    width={190}
                    placeholder="Select action..."
                    isValid={!noAction}
                    onValueChanged={e => handleRowChange(row.rowId, { field: e.value as FieldKind })}
                />
                {!noAction && (isCreateModule ? (
                    <SelectBox
                        items={moduleOptions}
                        displayExpr="label"
                        valueExpr="value"
                        value={row.targetModule}
                        placeholder="Select module..."
                        width={220}
                        isValid={!!row.targetModule}
                        onValueChanged={e => handleRowChange(row.rowId, { targetModule: e.value ?? "" })}
                    />
                ) : (
                    <>
                        {isTaskAction && (
                            <SelectBox
                                items={PHASE_ACTION_OPTIONS}
                                displayExpr="label"
                                valueExpr="value"
                                value={row.phaseAction}
                                placeholder="Select task action..."
                                width={180}
                                isValid={!!row.phaseAction}
                                onValueChanged={e => handleRowChange(row.rowId, { phaseAction: e.value ?? "" })}
                            />
                        )}
                        <span>{isTaskAction ? "on" : "of"}</span>
                        {external ? <strong>this task</strong> : (
                            <RowTargetSelect
                                targets={targetOptions}
                                value={row.targetKey}
                                isSelectable={rowSelectablePredicate(row)}
                                disabledReason={disabledTargetReason(row)}
                                onChange={(key) => handleRowChange(row.rowId, { targetKey: key })}
                            />
                        )}
                        {showStatus && (
                            <>
                                <span>to</span>
                                <SelectBox
                                    items={statusOptions}
                                    displayExpr="label"
                                    valueExpr="value"
                                    value={row.targetStatus}
                                    placeholder="Select status..."
                                    width={220}
                                    disabled={!row.targetKey}
                                    isValid={!!row.targetStatus}
                                    onValueChanged={e => handleRowChange(row.rowId, { targetStatus: e.value ?? "" })}
                                />
                            </>
                        )}
                        {showDate && (
                            <>
                                <span>to</span>
                                <SelectBox
                                    items={dateSourceOptionsFor(row.trigger, row.dateSource)}
                                    displayExpr="label"
                                    valueExpr="value"
                                    value={row.dateSource}
                                    width={200}
                                    placeholder="Select date..."
                                    onValueChanged={e => handleRowChange(row.rowId, { dateSource: e.value ?? "" })}
                                />
                                <span>offset by</span>
                                <NumberBox
                                    // null is what clears a DevExtreme editor; undefined means
                                    // "no value supplied" and leaves the old number on screen.
                                    value={(row.offsetValue === "" ? null : Number(row.offsetValue)) as unknown as number | undefined}
                                    width={80}
                                    showSpinButtons={true}
                                    placeholder="0"
                                    onValueChanged={e => handleRowChange(row.rowId, { offsetValue: e.value == null ? "" : String(e.value) })}
                                />
                                <SelectBox
                                    items={OFFSET_UNIT_OPTIONS}
                                    displayExpr="label"
                                    valueExpr="value"
                                    value={row.offsetUnit || DEFAULT_OFFSET_UNIT}
                                    width={110}
                                    onValueChanged={e => handleRowChange(row.rowId, { offsetUnit: e.value ?? DEFAULT_OFFSET_UNIT })}
                                />
                            </>
                        )}
                    </>
                ))}
            </div>
        );
    };

    const renderRunsCell = (row: TriggerRow, editing: boolean) => {
        if (!editing) return <span>{row.runLimit === 1 ? "once only" : "every time"}</span>;
        return (
            <SelectBox
                items={RUN_LIMIT_OPTIONS}
                displayExpr="label"
                valueExpr="value"
                value={row.runLimit === 1 ? RUN_LIMIT_ONCE : RUN_LIMIT_UNLIMITED}
                width="100%"
                onValueChanged={e => handleRowChange(row.rowId, { runLimit: e.value === RUN_LIMIT_ONCE ? 1 : null })}
            />
        );
    };

    const renderActionsCell = (row: TriggerRow, editing: boolean) => {
        const incomplete = ruleProblems(row).length > 0;
        return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
            {editing && row.field && (
                <>
                    <i
                        id={`trigger-help-${row.rowId}`}
                        className="dx-icon dx-icon-info"
                        data-testid="trigger-action-help"
                        style={{ color: "#888", cursor: "help" }}
                    />
                    <Tooltip
                        target={`#trigger-help-${row.rowId}`}
                        showEvent="mouseenter"
                        hideEvent="mouseleave"
                        hideOnOutsideClick={false}
                        position="top"
                    >
                        <div style={{ padding: "8px 10px", maxWidth: "280px", textAlign: "left", fontSize: "12px" }}>
                            <div style={{ marginBottom: "6px" }}>
                                {allTriggerOptions.find(o => o.trigger === row.trigger)?.description}
                            </div>
                            <div>{ACTION_HELP[row.field as string]}</div>
                        </div>
                    </Tooltip>
                </>
            )}
            {rowHasEdits(row) && (
                <Button
                    icon="revert"
                    stylingMode="text"
                    hint="Undo changes to this rule"
                    onClick={e => {
                        e.event?.stopPropagation();
                        handleRevertRow(row.rowId);
                    }}
                />
            )}
            <Button
                icon={editing ? "check" : "edit"}
                stylingMode="text"
                disabled={editing ? incomplete : openRuleIsIncomplete}
                hint={editing
                    ? (incomplete ? "Finish this rule first" : "Done editing")
                    : (openRuleIsIncomplete ? "Finish the open rule first" : "Edit rule")}
                onClick={e => {
                    e.event?.stopPropagation();
                    setEditingRowId(editing ? null : row.rowId);
                }}
            />
            <Button
                icon="trash"
                stylingMode="text"
                hint="Remove rule"
                onClick={e => {
                    e.event?.stopPropagation();
                    handleRemoveRow(row.rowId);
                }}
            />
        </div>
        );
    };

    const editingRow = rows.find(r => r.rowId === editingRowId) ?? null;
    const openRuleIsIncomplete = editingRow != null && ruleProblems(editingRow).length > 0;

    const renderRuleRow = (row: TriggerRow) => {
        const editing = row.rowId === editingRowId;
        const problems = ruleProblems(row);
        return (
            <tr
                key={row.rowId}
                data-testid="trigger-rule-row"
                onClick={() => {
                    if (editing) return;
                    // Leaving a half-finished rule behind is what the "cannot save" list used to
                    // explain after the fact; it simply is not allowed now.
                    if (openRuleIsIncomplete) return;
                    setEditingRowId(row.rowId);
                }}
                style={{
                    borderBottom: "1px solid #eee",
                    cursor: editing ? "default" : "pointer",
                    // An incomplete rule is the reason save is blocked, so it has to be findable in
                    // a long list, not just described underneath it.
                    borderLeft: problems.length > 0 ? `3px solid ${PROBLEM_COLOR}` : "3px solid transparent",
                    background: editing ? "#f5f9ff" : undefined
                }}
            >
                <td style={CELL_STYLE}>{renderWhenCell(row, editing)}</td>
                <td style={CELL_STYLE}>{renderThenCell(row, editing)}</td>
                <td style={CELL_STYLE}>{renderRunsCell(row, editing)}</td>
                <td style={CELL_STYLE}>{renderActionsCell(row, editing)}</td>
            </tr>
        );
    };

    const renderSection = (testId: string, title: string, description: string, sectionRows: TriggerRow[], external: boolean) => (
        <div data-testid={testId} style={{ marginBottom: "28px" }}>
            <div style={{ fontWeight: 600, marginBottom: "4px" }}>{title}</div>
            <div style={{ color: "#666", fontSize: "13px", marginBottom: "8px" }}>{description}</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        <th style={{ ...HEADER_CELL_STYLE, width: "22%" }}>When</th>
                        <th style={HEADER_CELL_STYLE}>Then</th>
                        <th style={{ ...HEADER_CELL_STYLE, width: "140px" }}>Runs</th>
                        <th style={{ ...HEADER_CELL_STYLE, width: "110px" }} />
                    </tr>
                </thead>
                <tbody>
                    {sectionRows.length === 0 && (
                        <tr>
                            <td style={{ ...CELL_STYLE, color: "#888" }} colSpan={4}>No rules configured.</td>
                        </tr>
                    )}
                    {sectionRows.map(renderRuleRow)}
                </tbody>
            </table>

            <div style={{ marginTop: "12px" }}>
                <Button
                    icon="add"
                    text="Add Action"
                    stylingMode="outlined"
                    onClick={() => handleAddRow(external)}
                    hint={openRuleIsIncomplete ? "Finish the open rule first" : undefined}
                    disabled={targetOptions.length === 0 || openRuleIsIncomplete}
                />
            </div>
        </div>
    );

    return (
        <div className="template-tab">
            <div className="template-tab-body">
            <p style={{ marginBottom: "20px", color: "#666" }}>
                Each rule reads as a sentence: when something happens, do one thing. Click a rule to
                edit it in place. Hover the info icon for what the chosen event and action do.
            </p>

            {modules == null ? (
                <p style={{ color: "#888" }}>Loading targets...</p>
            ) : (
                <>
                    {renderSection(
                        "task-triggers",
                        isTask ? "Task triggers" : "Subtask triggers",
                        `Something happens on this ${triggerLabel}. Targets can be any task or subtask from any module in the grant.`,
                        rows.filter(r => !isExternal(r)),
                        false
                    )}
                    {isTask && renderSection(
                        "external-triggers",
                        "External triggers",
                        "Something happens to the grant outside the tasklist, such as its status changing in IMPAC II. These rules act on this task.",
                        rows.filter(isExternal),
                        true
                    )}
                </>
            )}

            </div>

            <div className="template-tab-footer">
                <Button
                    text={isDirty ? "Save Triggers *" : "Save Triggers"}
                    type="default"
                    stylingMode="contained"
                    onClick={handleSave}
                    disabled={saveDisabled || !isDirty}
                />
                {isDirty && (
                    <Button text="Discard Changes" type="normal" stylingMode="outlined" onClick={handleDiscard} />
                )}
                {isDirty && !saveDisabled && (
                    <span style={{ color: "#f0ad4e", fontSize: "12px", marginLeft: "8px" }}>
                        Unsaved changes
                    </span>
                )}
            </div>
        </div>
    );
}
